import React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import PersonIcon from "@mui/icons-material/Person";
import Box from "@mui/material/Box";
import logo from "../logo.png";  

interface HeadBarProps {
  darkMode: boolean;
  toggleDarkMode: () => void;
  userId?: string;
  userDisplayName?: string;
}

const HeadBar: React.FC<HeadBarProps> = ({ darkMode, toggleDarkMode, userId, userDisplayName }) => {
  // Check if user is guest (either "guest" or starts with "guest_")
  const isGuest = !userId || userId === "guest" || userId.startsWith("guest_");
  const authenticatedLabel = userDisplayName?.trim() || userId || "Utente";
  const loginUrl = process.env.REACT_APP_LOGIN_URL || "https://boatsconnect.glueglue.dev/index";

  const handleLoginClick = () => {
    window.location.href = loginUrl;
  };

  return (
    <AppBar
      position="fixed"
      color="default"
      sx={{
        zIndex: (theme) => theme.zIndex.appBar + 1,
        // Riduzione altezza headbar su mobile
        minHeight: { xs: '50px', sm: '64px' },
        height: { xs: '50px', sm: '64px' },
      }}
    >
      <Toolbar 
        sx={{ 
          justifyContent: "center", 
          position: "sticky",
          // Riduzione padding e altezza toolbar su mobile
          minHeight: { xs: '50px !important', sm: '64px !important' },
          padding: { xs: '0 8px !important', sm: '0 16px !important' },
        }}
      >
        {/* Container per bottone login/user a SINISTRA */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: { xs: 8, sm: 16 },
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 1,
            zIndex: 1000,
          }}
        >
          {isGuest ? (
            // User is guest - show "Accedi" button
            <Button
              variant="contained"
              startIcon={<PersonIcon />}
              onClick={handleLoginClick}
              sx={{
                backgroundColor: darkMode ? '#098988' : '#ea8b3e',
                color: 'white',
                fontSize: { xs: '12px', sm: '14px' }, // Font più piccolo su mobile
                fontWeight: 600,
                borderRadius: '20px',
                textTransform: 'none',
                padding: { xs: '4px 12px', sm: '6px 16px' }, // Padding ridotto su mobile
                minHeight: { xs: '32px', sm: 'auto' }, // Altezza ridotta su mobile
                '&:hover': {
                  backgroundColor: darkMode ? '#087776' : '#d67a2e',
                },
                // Riduzione dimensione icona su mobile
                '& .MuiButton-startIcon': {
                  marginRight: { xs: '4px', sm: '8px' },
                  '& svg': {
                    fontSize: { xs: '18px', sm: '20px' } // Icona più piccola su mobile
                  }
                }
              }}
            >
              Accedi
            </Button>
          ) : (
            // User logged in - show user full name when available
            <Button
              variant="contained"
              startIcon={<PersonIcon />}
              sx={{
                backgroundColor: darkMode ? '#098988' : '#ea8b3e',
                color: 'white',
                fontSize: { xs: '12px', sm: '14px' }, // Font più piccolo su mobile
                fontWeight: 600,
                borderRadius: '20px',
                textTransform: 'none',
                padding: { xs: '4px 12px', sm: '6px 16px' }, // Padding ridotto su mobile
                minHeight: { xs: '32px', sm: 'auto' }, // Altezza ridotta su mobile
                '&:hover': {
                  backgroundColor: darkMode ? '#087776' : '#d67a2e',
                },
                // Riduzione dimensione icona su mobile
                '& .MuiButton-startIcon': {
                  marginRight: { xs: '4px', sm: '8px' },
                  '& svg': {
                    fontSize: { xs: '18px', sm: '20px' } // Icona più piccola su mobile
                  }
                }
              }}
            >
              {authenticatedLabel}
            </Button>
          )}
        </Box>

        {/* Logo centrato - ridotto su mobile */}
        <Box
          component="a"
          href={loginUrl}
          aria-label="Vai alla home del portale"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 0,
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              height: { xs: 30, sm: 40 }, // Logo più piccolo su mobile
              width: 'auto',
              cursor: "pointer",
            }}
          />
        </Box>
        
        {/* Container per theme toggle a DESTRA */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: { xs: 8, sm: 16 },
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 1,
            zIndex: 1000,
          }}
        >
          <IconButton
            onClick={toggleDarkMode}
            aria-label="toggle dark mode"
            sx={{
              // Riduzione dimensione IconButton su mobile
              padding: { xs: '6px', sm: '8px' },
              width: { xs: '32px', sm: '40px' },
              height: { xs: '32px', sm: '40px' },
              '& svg': {
                fontSize: { xs: '20px', sm: '24px' } // Icona più piccola su mobile
              }
            }}
          >
            {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default HeadBar;